import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  Activity, AlertCircle, ArrowRight, CalendarCheck, CalendarClock, CheckCircle2, ChevronDown, ClipboardList,
  Clock, Info, Landmark, PiggyBank, Receipt, RefreshCw, Sparkles, Users, Wallet,
} from 'lucide-react';
import {
  HueIcon, KpiTile, RailList, RailRow, EmptyState, ErrorState, Tooltip, monthLabel,
} from './design';
import { plural } from '../../components/ui';
import { Connections } from './Connections';

// The aged care overview.
//
// It opens on the quarter, because Support at Home runs on it: every participant's budget is a
// quarterly budget, claims fall due 60 days after the quarter ends, and whatever is left over above
// the carryover limit is lost. The band at the top sets how far through the quarter we are against
// how far through the budgets we are. Under it, the work: what is overdue or due soon, and the dates
// coming up. Then the money (budgets, claims, statements) and the week's visits.
//
// Colour follows one rule across the section: teal is aged care (the section's own colour, from the
// hue-teal class on <main>), indigo is an action, and red, amber and green only ever mean state.

// ── Small helpers ────────────────────────────────────────────────────────────
const dollars = (v) => `${Number(v) < 0 ? '-' : ''}$${Math.abs(Math.round(Number(v) || 0)).toLocaleString('en-AU')}`;
const count = (v) => Number(v || 0).toLocaleString('en-AU');
const parseDay = (s) => { const [y, m, d] = String(s).slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); };
const dayDiff = (from, to) => Math.round((parseDay(to) - parseDay(from)) / 86400000);
const longDate = (s) => parseDay(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'long' });
const monthShort = (s) => parseDay(s).toLocaleDateString('en-AU', { month: 'short' });
const inDays = (from, to) => {
  const d = dayDiff(from, to);
  if (d < 0) return `${plural(-d, 'day')} ago`;
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  return `in ${plural(d, 'day')}`;
};

// Bars grow once, when the numbers first arrive: the one moment of motion on the screen, and it
// shows the quantity rather than decorating it. Reduced motion skips it (motion-reduce utilities).
function useGrown() {
  const [grown, setGrown] = useState(false);
  useEffect(() => { const t = requestAnimationFrame(() => setGrown(true)); return () => cancelAnimationFrame(t); }, []);
  return grown;
}
const GROW = 'origin-left transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none';

// ── Where each kind of work is done ───────────────────────────────────────────
const TAB_ACTION = {
  statements: 'Open statements',
  sirs: 'Open SIRS incidents',
  claims: 'Open claims',
  compliance: 'Open worker compliance',
  services: 'Open service list',
  careplans: 'Open care plans',
  funding: 'Open client funding',
  participants: 'Open participants',
  budgets: 'Open budgets',
  reports: 'Open government reports',
  items: 'Open items and equipment',
  caremgmt: 'Open care management',
  chsp: 'Open CHSP',
  dex: 'Open DEX',
  referrals: 'Open referrals',
};

// ── The operations checklist, folded into "what needs you" ────────────────────
// A pending step becomes a row phrased as the work, and a done step becomes a few words on the "All
// clear" line. A step the attention list already raises is left out, so nothing is said twice.
// Older servers sent the steps without `kind`; read it from the label.
const kindOf = (step) => {
  if (step.kind) return step.kind;
  const l = String(step.label || '').toLowerCase();
  if (l.includes('service list')) return 'service_list';
  if (l.includes('care plan')) return 'care_plans';
  if (l.includes('care management')) return 'care_mgmt';
  if (l.includes('sirs')) return 'sirs';
  if (l.includes('statement')) return 'statements';
  if (l.includes('claim')) return 'claims';
  return null;
};
// The attention list and the checklist name the same obligation differently in one place: the
// attention list's "less than 15 minutes of care management" covers the checklist's "care
// management not recorded".
const SAME_AS = { care_management: 'care_mgmt' };

const lastMonth = () => {
  const d = new Date();
  const p = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}`;
};
const monthName = (period) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(period || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString('en-AU', { month: 'long' }) : "Last month's";
};

const STEPS = {
  service_list: {
    tab: 'services', action: 'Open service list',
    todo: () => ({
      title: 'Support at Home service list not loaded',
      detail: 'Load the official list so the codes you deliver and claim match the ones the department expects.',
    }),
    done: () => 'service list loaded',
  },
  care_plans: {
    tab: 'careplans', action: 'Open care plans',
    applies: ({ q }) => q.carePlanPct != null,
    todo: ({ step, q }) => ({
      title: step.count != null
        ? `${plural(step.count, 'funded participant')} without a care plan`
        : 'Not every funded participant has a care plan',
      detail: `Care plans cover ${q.carePlanPct}% of funded participants.`,
    }),
    done: () => 'care plans 100%',
  },
  care_mgmt: {
    tab: 'caremgmt', action: 'Log care management',
    applies: ({ q }) => q.cmPct != null,
    todo: ({ step, q }) => ({
      title: step.count != null
        ? `Care management not recorded this month for ${plural(step.count, 'participant')}`
        : 'Care management not recorded this month for every participant',
      detail: `Each funded participant needs at least one care management activity a month, and ${q.cmPct}% have one so far. Log it from the participant's funding plan.`,
    }),
    done: () => 'care management 100% this month',
  },
  sirs: {
    tab: 'sirs', action: 'Open SIRS incidents', severity: 'overdue',
    todo: ({ step, q }) => ({
      title: `${plural(step.count != null ? step.count : (q.sirsOverdue || 0), 'SIRS notification')} overdue`,
      detail: 'Report through the My Aged Care provider portal, then record it here.',
    }),
    done: () => 'no overdue SIRS notifications',
  },
  statements: {
    tab: 'statements', action: 'Open statements',
    applies: ({ ctx }) => ctx.activeClients > 0,
    todo: ({ step }) => ({
      title: `${monthLabel(step.period || lastMonth())} statements not generated`,
      detail: 'Every participant is owed a statement for every month, even where no services were delivered.',
    }),
    done: ({ step }) => `${monthName(step.period || lastMonth())} statements generated`,
  },
  claims: {
    tab: 'claims', action: 'Open claims',
    todo: ({ step, ctx }) => ({
      title: `${plural(step.count != null ? step.count : ctx.pendingClaims, 'claim')} awaiting submission or reconciliation`,
      detail: 'Lodge draft claims with Services Australia, and reconcile submitted ones once the outcome is back.',
    }),
    done: () => 'no claims waiting',
  },
};

const RANK = { overdue: 0, soon: 1, open: 2, ok: 3 };

function buildList(attention, dash) {
  const items = (attention && attention.items) || [];
  const raised = new Set(items.map((it) => SAME_AS[it.kind] || it.kind));
  const rows = items.map((it, i) => ({
    key: it.id ? `o-${it.id}` : `a-${it.kind || 'item'}-${i}`, severity: it.severity, title: it.title, detail: it.detail,
    tab: it.tab, due: it.due, action: TAB_ACTION[it.tab],
    // An owner whose user has no name on file is still an owner: shown as "Someone", never their id.
    id: it.id || null, ownerName: it.ownerName || (it.ownerId ? 'Someone' : null), statutory: !!it.statutory, escalatedAt: it.escalatedAt || null,
  }));
  const clear = [];
  if (dash) {
    const args0 = {
      q: dash.quality || {},
      ctx: { activeClients: Number(dash.activeClients || 0), pendingClaims: Number(dash.pendingClaims || 0) },
    };
    (dash.checklist || []).forEach((step) => {
      const kind = kindOf(step);
      const def = STEPS[kind];
      if (!def || raised.has(kind)) return;
      const args = { ...args0, step };
      if (def.applies && !def.applies(args)) return;
      if (step.done) { clear.push(def.done(args)); return; }
      const t = def.todo(args);
      rows.push({
        key: `c-${kind}`, severity: def.severity || 'open', title: t.title, detail: t.detail,
        tab: step.tab || def.tab, due: null, action: def.action,
      });
    });
  }
  // Stable sort: the server already ordered its items by deadline within each severity.
  rows.sort((a, b) => (RANK[a.severity] ?? 2) - (RANK[b.severity] ?? 2));
  return {
    rows, clear, today: (attention && attention.today) || new Date().toISOString().slice(0, 10),
    snoozed: (attention && attention.snoozed) || [], done: (attention && attention.done) || [],
  };
}

// The deadline, relative to the server's today, as a pill in the row's own state colour.
const DUE_PILL = {
  overdue: 'bg-red-50 text-red-800 ring-red-200/70',
  soon: 'bg-amber-50 text-amber-800 ring-amber-200/70',
};
function dueLabel(row, today) {
  if (!row.due) return null;
  const d = Math.round((new Date(row.due) - new Date(today)) / 86400000);
  if (d < 0) return `${plural(-d, 'day')} overdue`;
  // A SIRS window can close earlier on the day it is due; it is overdue then, not "due today".
  if (d === 0) return row.severity === 'overdue' ? 'overdue' : 'due today';
  return `due in ${plural(d, 'day')}`;
}

// ── The quarter ──────────────────────────────────────────────────────────────
// Two bars on one timeline: how much of the quarter has gone, and how much of the participants'
// budgets has been delivered. When the second runs well behind the first, participants are not
// getting the care they are funded for and money above the carryover limit will be lost; when it
// runs ahead, budgets will run out before the quarter does.
const QUARTER_NAMES = ['January to March', 'April to June', 'July to September', 'October to December'];

function QuarterBand({ quarter, budgets, updatedAt, refreshing, onRefresh, onGoTo }) {
  const grown = useGrown();
  const start = parseDay(quarter.start);
  const title = `${QUARTER_NAMES[Math.floor(start.getMonth() / 3)]} ${start.getFullYear()}`;
  const pace = quarter.daysTotal ? quarter.daysElapsed / quarter.daysTotal : 0;
  const left = quarter.daysLeft;

  // Month boundaries on the timeline.
  const ticks = [0, 1, 2].map((i) => {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const offset = Math.round((d - start) / 86400000);
    return { label: d.toLocaleDateString('en-AU', { month: 'short' }), at: offset / quarter.daysTotal };
  });

  const has = budgets && budgets.participants > 0;
  const used = has ? Math.min(1, budgets.spent / (budgets.pool || 1)) : 0;
  const ahead = has && budgets.spent > budgets.pool;
  const usedTone = !has ? 'bg-slate-300'
    : ahead ? 'bg-red-500'
      : used > pace + 0.15 ? 'bg-amber-500'
        : 'hue-bar';

  let reading = null;
  if (has) {
    const gap = Math.round((used - pace) * 100);
    reading = ahead
      ? `Budgets are overspent by ${dollars(budgets.spent - budgets.pool)} in total.`
      : gap > 15 ? `Spending is ${gap} points ahead of the quarter.`
        : gap < -15 ? `Spending is ${-gap} points behind the quarter.`
          : 'Spending is keeping pace with the quarter.';
  }

  return (
    <section
      aria-labelledby="ac-quarter-title"
      className="relative overflow-hidden rounded-xl border px-5 py-5 sm:px-6"
      style={{ background: 'linear-gradient(180deg, rgb(var(--hue) / .09), rgb(var(--hue) / .03))', borderColor: 'rgb(var(--hue) / .22)' }}
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:gap-10">
        <div className="lg:w-[20rem] shrink-0">
          <h2 id="ac-quarter-title" className="font-display text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] text-slate-900 [text-wrap:balance]">
            {title}
          </h2>
          <p className="mt-1.5 text-[13px] text-slate-600">
            Quarter {quarter.number} of {quarter.fy}.{' '}
            <span className="font-medium text-slate-800">
              {left > 1 ? `${left} days left` : left === 1 ? '1 day left' : 'Last day of the quarter'}
            </span>
          </p>
          <div className="mt-3 flex items-center gap-2 text-[12px] text-slate-500">
            <span>Updated {updatedAt ? updatedAt.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : 'just now'}</span>
            <button
              type="button" onClick={onRefresh} disabled={refreshing}
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-primary-700 hover:bg-white/70 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              <RefreshCw size={12} className={clsx(refreshing && 'animate-spin motion-reduce:animate-none')} aria-hidden="true" />
              {refreshing ? 'Refreshing' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="relative">
            <TrackRow label="Quarter gone" value={`${Math.round(pace * 100)}%`}>
              <div className={clsx('h-full rounded-full hue-bar', GROW)} style={{ transform: `scaleX(${grown ? pace : 0})` }} />
            </TrackRow>
            <TrackRow
              label="Budgets used"
              value={has ? `${budgets.usedPct}%` : '—'}
              className="mt-3"
              label2={has ? `${dollars(budgets.spent)} of ${dollars(budgets.pool)}` : null}
            >
              <div className={clsx('h-full rounded-full', usedTone, GROW)} style={{ transform: `scaleX(${grown ? used : 0})` }} />
            </TrackRow>
            {/* Today: one line through both bars, so the gap between them reads at a glance. */}
            <div className="pointer-events-none absolute top-0 bottom-0 left-[7.5rem] right-[4.5rem]" aria-hidden="true">
              <div className="absolute -top-1 -bottom-1 w-px bg-slate-900/60" style={{ left: `${pace * 100}%` }} />
            </div>
          </div>
          <div className="relative ml-[7.5rem] mr-[4.5rem] mt-1.5 h-4 text-[11px] text-slate-500" aria-hidden="true">
            {ticks.map((t) => (
              <span key={t.label} className="absolute" style={{ left: `${t.at * 100}%` }}>{t.label}</span>
            ))}
          </div>
          <p className="mt-2 text-[13px] text-slate-700">
            {has ? (
              <>
                {reading}{' '}
                <span className="text-slate-500">
                  Across {plural(budgets.participants, 'participant')}. Claims for this quarter are due {longDate(quarter.claimDue)}.
                </span>
              </>
            ) : (
              <>
                No quarterly budget is entered for this quarter yet.{' '}
                <button type="button" onClick={() => onGoTo('budgets')} className="font-medium text-primary-700 hover:underline underline-offset-2">
                  Enter budgets
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </section>
  );
}
function TrackRow({ label, label2, value, className, children }) {
  return (
    <div className={clsx('flex items-center gap-3', className)}>
      <div className="w-[6.75rem] shrink-0 text-[12px] leading-tight text-slate-600">
        {label}
        {label2 && <span className="block text-[11px] text-slate-500 tabular-nums">{label2}</span>}
      </div>
      <div className="flex-1 h-3 rounded-full bg-white/80 ring-1 ring-inset ring-slate-900/[0.06] overflow-hidden">{children}</div>
      <div className="w-[3.75rem] shrink-0 text-right font-display text-[17px] font-semibold tabular-nums text-slate-900">{value}</div>
    </div>
  );
}

// ── What needs you ───────────────────────────────────────────────────────────
// A list with a severity mark rather than a grid of cards: cards would give an overdue statement
// and a worker check expiring next month the same weight. Every row carries its due date, because
// in this domain the deadline is the information. The first six show; the rest are one click away.
const SHOW = 6;
// What can be done to one item without leaving the overview: mark it done (for a step done in a
// government portal), snooze it to a later day (a legal deadline only up to its due date), or give
// it an owner. Sits beside the row, not inside it, because the row itself is a button.
function ItemActions({ row, today, owners, onChanged }) {
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState('');
  const [until, setUntil] = useState('');
  const [owner, setOwner] = useState('');
  const [busy, setBusy] = useState(false);
  if (!row.id) return null;
  const act = async (action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/aged-care/attention/${row.id}/action`, { action, note, until, ownerId: owner || null, ...extra });
      toast.success(action === 'done' ? 'Marked done' : action === 'snooze' ? 'Snoozed' : action === 'assign' ? 'Owner set' : 'Updated');
      setMode(null); setNote(''); setUntil(''); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not update it'); } finally { setBusy(false); }
  };
  const link = 'text-[12px] font-medium text-primary-700 hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded';
  const maxSnooze = row.statutory && row.due ? row.due : undefined;
  return (
    <div className="px-4 pb-2.5 -mt-1 pl-11">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
        {row.ownerName && <span>Owner: <span className="text-slate-700">{row.ownerName}</span></span>}
        {row.escalatedAt && <span className="text-red-700 font-medium">Escalated</span>}
        <button type="button" className={link} onClick={() => setMode(mode === 'done' ? null : 'done')} aria-expanded={mode === 'done'}>Mark done</button>
        <button type="button" className={link} onClick={() => setMode(mode === 'snooze' ? null : 'snooze')} aria-expanded={mode === 'snooze'}>Snooze</button>
        <button type="button" className={link} onClick={() => setMode(mode === 'owner' ? null : 'owner')} aria-expanded={mode === 'owner'}>{row.ownerName ? 'Change owner' : 'Give it an owner'}</button>
      </div>
      {mode === 'done' && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="text-[12px] text-slate-600 flex-1 min-w-[14rem]">What was done (optional)
            <input className="input-field mt-1" value={note} onChange={(e) => setNote(e.target.value)} placeholder="For example: recorded in the Provider Portal on 12 Oct" />
          </label>
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={() => act('done')}>Mark done</button>
        </div>
      )}
      {mode === 'snooze' && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="text-[12px] text-slate-600">Remind me again on
            <input type="date" className="input-field mt-1" value={until} min={today} max={maxSnooze} onChange={(e) => setUntil(e.target.value)} />
          </label>
          <label className="text-[12px] text-slate-600 flex-1 min-w-[12rem]">Why (optional)
            <input className="input-field mt-1" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <button type="button" className="btn-primary btn-sm" disabled={busy || !until} onClick={() => act('snooze')}>Snooze</button>
          {maxSnooze && <p className="w-full text-[11.5px] text-slate-500">A legal deadline can be snoozed up to its due date, not past it.</p>}
        </div>
      )}
      {mode === 'owner' && (
        <div className="mt-2 flex flex-wrap items-end gap-2">
          <label className="text-[12px] text-slate-600">Owner
            <select className="input-field mt-1" value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">Nobody</option>
              {owners.map((o) => <option key={o.id} value={o.id}>{o.name || 'Someone'}</option>)}
            </select>
          </label>
          <button type="button" className="btn-primary btn-sm" disabled={busy} onClick={() => act('assign')}>Save</button>
        </div>
      )}
    </div>
  );
}

// Items set aside: snoozed to a later day, or marked done by hand. Each can be brought back.
function SetAside({ title, items, render, onChanged }) {
  const [open, setOpen] = useState(false);
  if (!items.length) return null;
  const reopen = async (it) => {
    try { await api.post(`/aged-care/attention/${it.id}/action`, { action: 'reopen' }); toast.success('Back on the list'); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not reopen it'); }
  };
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="text-[13px] font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1">
        {title} ({items.length})
        <ChevronDown size={14} className={clsx('transition-transform motion-reduce:transition-none', open && 'rotate-180')} aria-hidden="true" />
      </button>
      {open && (
        <ul className="mt-1.5 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {items.map((it) => (
            <li key={it.id} className="px-4 py-2.5 flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-[13px] text-slate-800">{it.title}<span className="block text-[12px] text-slate-500">{render(it)}</span></span>
              <button type="button" className="text-[12px] font-medium text-primary-700 hover:underline underline-offset-2" onClick={() => reopen(it)}>Put it back</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// How reminders reach people: the daily email, push to their phones, a text message for the
// critical rules (referral response, departure, the DEX monthly report, a Priority 1 serious
// incident), and the days before a due date they start (then daily once overdue).
function DailyEmailSwitch() {
  const [s, setS] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState('');
  const load = useCallback(() => {
    setFailed(false);
    api.get('/aged-care/reminders/settings').then((r) => { setS(r.data); setLeads((r.data.lead_days || []).join(', ')); }).catch(() => setFailed(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  // A failed load says so in one line, rather than the reminder settings silently disappearing.
  if (failed) {
    return (
      <span className="text-[12px] text-red-800" role="alert">
        Reminder settings did not load. <button type="button" className="underline underline-offset-2" onClick={load}>Try again</button>
      </span>
    );
  }
  if (!s) return null;
  const save = async (patch, msg) => {
    try { const r = await api.put('/aged-care/reminders/settings', { ...s, ...patch }); setS(r.data); setLeads((r.data.lead_days || []).join(', ')); toast.success(msg); }
    catch (e) { toast.error(e.response?.data?.message || 'Could not change it'); }
  };
  const Toggle = ({ k, label, on, off }) => (
    <label className="flex items-center gap-2 text-[12.5px] text-slate-700 cursor-pointer">
      <input type="checkbox" checked={!!s[k]} onChange={() => save({ [k]: !s[k] }, s[k] ? off : on)} /> {label}
    </label>
  );
  return (
    <span className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="text-[12px] text-slate-500 hover:text-slate-800">
        Reminders: <span className="font-medium text-slate-700">{[s.daily_digest && 'daily email', s.push_reminders && 'push', s.sms_critical && 'texts'].filter(Boolean).join(', ') || 'in the app only'}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-80 rounded-xl border border-slate-200 bg-white p-3 space-y-2 shadow-card-deep text-left">
          <Toggle k="daily_digest" label="Daily email to admins and owners" on="Daily email on" off="Daily email off" />
          <Toggle k="push_reminders" label="Push to phones at the lead times" on="Push on" off="Push off" />
          <Toggle k="sms_critical" label="Text message for critical rules" on="Texts on (uses your SMS account)" off="Texts off" />
          <label className="block text-[12.5px] text-slate-700">
            Remind this many days before a due date
            <input aria-label="Days before" className="input-field !py-1 !text-[12px] mt-1" value={leads} onChange={(e) => setLeads(e.target.value)}
              onBlur={() => { const v = leads.split(',').map((x) => x.trim()).filter(Boolean); save({ lead_days: v }, 'Lead times saved'); }} />
            <span className="text-[11.5px] text-slate-500">For example 7, 3, 1, 0. Then every day once overdue.</span>
          </label>
        </div>
      )}
    </span>
  );
}

function NeedsYou({ list, onGoTo, onChanged }) {
  const { rows, clear, today } = list;
  const [all, setAll] = useState(false);
  const [owners, setOwners] = useState([]);
  useEffect(() => { api.get('/aged-care/attention/owners').then((r) => setOwners(r.data || [])).catch(() => {}); }, []);
  const setAside = (
    <>
      <SetAside title="Snoozed" items={list.snoozed || []} onChanged={onChanged}
        render={(it) => `Until ${new Date(`${it.snoozedUntil}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}${it.snoozeReason ? `: ${it.snoozeReason}` : ''}`} />
      <SetAside title="Marked done" items={list.done || []} onChanged={onChanged}
        render={(it) => `Done${it.doneNote ? `: ${it.doneNote}` : ''}`} />
    </>
  );
  const overdue = rows.filter((r) => r.severity === 'overdue').length;
  const soon = rows.filter((r) => r.severity === 'soon').length;

  const allClear = clear.length > 0 && (
    <p className="mt-2.5 text-[13px] text-slate-600">
      <CheckCircle2 size={14} className="inline -mt-0.5 mr-1 text-emerald-600" aria-hidden="true" />
      <span className="font-medium text-slate-700">All clear:</span> {clear.join(', ')}.
    </p>
  );

  // An empty list here is a GOOD state, so it reads as calm rather than as a blank screen.
  if (!rows.length) {
    return (
      <section aria-labelledby="ac-needs-title">
        <h2 id="ac-needs-title" className="text-[17px] font-semibold text-slate-900 mb-3">What needs you</h2>
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-8 text-center">
          <span className="mx-auto mb-3 flex w-10 h-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600" aria-hidden="true">
            <CheckCircle2 size={20} />
          </span>
          <p className="text-[15px] font-medium text-slate-800">Nothing is overdue</p>
          <p className="text-[13px] text-slate-500 mt-1">
            Statements are issued, incidents are reported, and no claim is past its deadline.
          </p>
        </div>
        {allClear}
        {setAside}
      </section>
    );
  }

  const heading = overdue > 0
    ? `${plural(overdue, 'thing')} overdue`
    : soon > 0 ? `${plural(soon, 'thing')} due soon` : `${plural(rows.length, 'thing')} to do`;
  const shown = all ? rows : rows.slice(0, SHOW);

  return (
    <section aria-labelledby="ac-needs-title">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
        <h2 id="ac-needs-title" className="text-[17px] font-semibold text-slate-900">{heading}</h2>
        {overdue > 0 && soon > 0 && <span className="text-[13px] text-slate-500">and {soon} due soon</span>}
        <span className="ml-auto"><DailyEmailSwitch /></span>
      </div>

      <RailList>
        {shown.map((row) => {
          const due = dueLabel(row, today);
          const right = due
            ? (
              <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-medium tabular-nums ring-1 ring-inset whitespace-nowrap',
                DUE_PILL[row.severity] || 'bg-slate-50 text-slate-600 ring-slate-200')}
              >
                {due}
              </span>
            )
            : row.action
              ? (
                <span className="inline-flex items-center gap-1 font-medium text-primary-700 whitespace-nowrap">
                  {row.action}<ArrowRight size={14} aria-hidden="true" />
                </span>
              )
              : null;
          return (
            <div key={row.key}>
              <RailRow
                severity={row.severity}
                title={row.title}
                detail={row.detail}
                right={right}
                onClick={row.tab ? () => onGoTo?.(row.tab) : undefined}
              />
              <ItemActions row={row} today={today} owners={owners} onChanged={onChanged} />
            </div>
          );
        })}
        {rows.length > SHOW && (
          <button
            type="button" onClick={() => setAll((v) => !v)} aria-expanded={all}
            className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-[13px] font-medium text-primary-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
          >
            {all ? 'Show fewer' : `Show all ${rows.length}`}
            <ChevronDown size={15} className={clsx('transition-transform motion-reduce:transition-none', all && 'rotate-180')} aria-hidden="true" />
          </button>
        )}
      </RailList>
      {allClear}
      {setAside}
    </section>
  );
}

// ── Panel shell ──────────────────────────────────────────────────────────────
// One container for every panel on the overview: white, one border, the section's teal in the
// icon tile. `link` names where the panel's detail lives.
function Card({ id, title, icon, meta, link, onGoTo, children, className, bodyClass = 'p-4' }) {
  return (
    <section aria-labelledby={id} className={clsx('rounded-xl border border-slate-200 bg-white overflow-hidden flex flex-col', className)}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2.5 min-w-0">
        <HueIcon icon={icon} size="sm" />
        <h2 id={id} className="text-[14px] font-semibold text-slate-900 truncate">{title}</h2>
        {meta && <div className="ml-auto flex items-center gap-1.5 flex-wrap justify-end">{meta}</div>}
      </div>
      <div className={clsx('flex-1', bodyClass)}>{children}</div>
      {link && (
        <button
          type="button" onClick={() => onGoTo(link.tab)}
          className="border-t border-slate-100 px-4 py-2.5 text-left text-[13px] font-medium text-primary-700 hover:bg-slate-50 inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
        >
          {link.label}<ArrowRight size={14} aria-hidden="true" />
        </button>
      )}
    </section>
  );
}
function PanelError({ what }) {
  return <p className="text-[13px] text-slate-500 py-6 text-center">Could not load {what}. Refresh to try again.</p>;
}

// ── Quality score ────────────────────────────────────────────────────────────
const QI_NOTE = 'Follows the shape of the home-care QI Program (from July 2026). The score starts at 100 and loses points for care management not delivered, participants without a care plan, overdue SIRS notifications and open complaints.';

function QualityCard({ q, onGoTo }) {
  const score = q ? Number(q.score) : null;
  const tone = score == null ? null : score >= 80 ? 'good' : score >= 55 ? 'warn' : 'bad';
  const TONE = {
    good: { text: 'text-emerald-700', bar: 'bg-emerald-500', word: 'On track' },
    warn: { text: 'text-amber-700', bar: 'bg-amber-500', word: 'Needs work' },
    bad: { text: 'text-red-700', bar: 'bg-red-500', word: 'Needs attention' },
  };
  const measures = q ? [
    { label: 'Care management this month', value: q.cmPct, show: (v) => `${v}%`, target: '100%', miss: (v) => v < 100, tab: 'caremgmt' },
    { label: 'Care plan coverage', value: q.carePlanPct, show: (v) => `${v}%`, target: '100%', miss: (v) => v < 100, tab: 'careplans' },
    { label: 'Overdue SIRS notifications', value: q.sirsOverdue, show: (v) => String(v), target: '0', miss: (v) => v > 0, red: true, tab: 'sirs' },
    { label: 'Open complaints', value: q.complaintsOpen, show: (v) => String(v), target: '0', miss: (v) => v > 0, tab: 'complaints' },
  ] : [];

  return (
    <Card
      id="ac-quality-title" title="Quality and compliance" icon={Activity} onGoTo={onGoTo}
      meta={(
        <Tooltip side="bottom" label={<span className="block w-64 whitespace-normal text-left font-normal leading-snug">{QI_NOTE}</span>}>
          <button
            type="button" aria-label="About the quality score" aria-describedby="ac-qi-note"
            className="inline-flex h-8 w-8 -my-1.5 items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <Info size={15} aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    >
      <span id="ac-qi-note" className="sr-only">{QI_NOTE}</span>
      {q ? (
        <>
          <div className="flex items-end justify-between gap-3">
            <div className={clsx('font-display text-[38px] font-bold leading-none tabular-nums tracking-[-0.02em]', TONE[tone].text)}>
              {score}<span className="text-[15px] font-medium text-slate-400"> / 100</span>
            </div>
            <span className={clsx('text-[12px] font-medium', TONE[tone].text)}>{TONE[tone].word}</span>
          </div>
          <div className="mt-2.5 h-1.5 rounded-full bg-slate-100 overflow-hidden" role="img" aria-label={`Quality score ${score} out of 100`}>
            <div className={clsx('h-full rounded-full', TONE[tone].bar)} style={{ width: `${Math.max(2, score)}%` }} />
          </div>
          <ul className="mt-3.5 space-y-1">
            {measures.map((m) => {
              const has = m.value != null;
              const miss = has && m.miss(Number(m.value));
              const Mark = !has ? null : miss ? AlertCircle : CheckCircle2;
              return (
                <li key={m.label}>
                  <button
                    type="button" onClick={() => onGoTo(m.tab)}
                    className="w-full -mx-1.5 px-1.5 py-1 rounded-md flex items-center gap-2 text-[13px] text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                  >
                    <span className="w-4 shrink-0" aria-hidden="true">
                      {Mark && <Mark size={15} className={miss ? (m.red ? 'text-red-600' : 'text-amber-600') : 'text-emerald-600'} />}
                    </span>
                    <span className="flex-1 min-w-0 text-slate-600">{m.label}</span>
                    <span className="tabular-nums whitespace-nowrap">
                      <span className={clsx('font-semibold', miss ? (m.red ? 'text-red-700' : 'text-amber-700') : 'text-slate-800')}>{has ? m.show(m.value) : '—'}</span>
                      <span className="text-slate-400"> / {m.target}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      ) : (
        <p className="text-[13px] text-slate-500 py-6 text-center">No quality data yet.</p>
      )}
    </Card>
  );
}

// ── Coming up ────────────────────────────────────────────────────────────────
// The fixed dates of the Support at Home year in one list: statements, the 60-day claim cut-off
// for each quarter, and the government financial reports. Each has always lived on its own screen.
function ComingUp({ items, today, onGoTo }) {
  return (
    <Card id="ac-coming-title" title="Coming up" icon={CalendarClock} onGoTo={onGoTo} bodyClass="py-1.5">
      {items == null ? <PanelError what="the dates" /> : !items.length ? (
        <p className="text-[13px] text-slate-500 py-6 px-4 text-center">Nothing falls due in the next 60 days.</p>
      ) : (
        <ul>
          {items.slice(0, 6).map((it) => {
            const d = dayDiff(today, it.date);
            return (
              <li key={`${it.date}-${it.title}`}>
                <button
                  type="button" onClick={() => onGoTo(it.tab)}
                  className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
                >
                  <span className={clsx('w-10 shrink-0 rounded-lg py-1 text-center leading-none', d <= 7 ? 'bg-amber-50 text-amber-800' : 'hue-chip')}>
                    <span className="block font-display text-[16px] font-semibold tabular-nums">{parseDay(it.date).getDate()}</span>
                    <span className="block text-[10px] mt-0.5">{monthShort(it.date)}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium text-slate-800 leading-snug">{it.title}</span>
                    <span className={clsx('block text-[12px]', d <= 7 ? 'text-amber-700' : 'text-slate-500')}>{inDays(today, it.date)}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

// ── Budgets this quarter ─────────────────────────────────────────────────────
// Every participant's quarterly budget, worst first, with a mark where spending should be by today.
const BUDGET_STATE = {
  over: { word: 'Over budget', pill: 'bg-red-50 text-red-800 ring-red-200/70', bar: 'bg-red-500' },
  fast: { word: 'Will run out', pill: 'bg-amber-50 text-amber-800 ring-amber-200/70', bar: 'bg-amber-500' },
  slow: { word: 'Underused', pill: 'bg-amber-50 text-amber-800 ring-amber-200/70', bar: 'hue-bar opacity-50' },
  on_track: { word: 'On track', pill: 'bg-emerald-50 text-emerald-800 ring-emerald-200/70', bar: 'hue-bar' },
};
function BudgetsCard({ budgets, quarter, onGoTo }) {
  const grown = useGrown();
  const pace = quarter && quarter.daysTotal ? quarter.daysElapsed / quarter.daysTotal : 0;
  const c = (budgets && budgets.counts) || {};
  const meta = budgets && budgets.participants > 0 && (
    <>
      {c.over > 0 && <StatePill cls={BUDGET_STATE.over.pill}>{c.over} over</StatePill>}
      {c.fast > 0 && <StatePill cls={BUDGET_STATE.fast.pill}>{c.fast} will run out</StatePill>}
      {c.slow > 0 && <StatePill cls={BUDGET_STATE.slow.pill}>{c.slow} underused</StatePill>}
      {!c.over && !c.fast && !c.slow && <StatePill cls={BUDGET_STATE.on_track.pill}>All on track</StatePill>}
    </>
  );
  return (
    <Card id="ac-budgets-title" title="Budgets this quarter" icon={Wallet} meta={meta} onGoTo={onGoTo}
      link={{ tab: 'budgets', label: 'Open budgets' }} bodyClass="px-4 pt-2 pb-3"
    >
      {budgets == null ? <PanelError what="the budgets" /> : budgets.participants === 0 ? (
        <div className="py-6 text-center">
          <p className="text-[13px] text-slate-700">No quarterly budgets entered for this quarter.</p>
          <p className="text-[12px] text-slate-500 mt-1">Enter each participant's budget from their Notice of Decision to track it here.</p>
        </div>
      ) : (
        <>
          <ul className="divide-y divide-slate-100">
            {budgets.list.map((b) => {
              const st = BUDGET_STATE[b.state] || BUDGET_STATE.on_track;
              const fill = Math.min(1, b.pool > 0 ? b.spent / b.pool : 0);
              return (
                <li key={b.clientId} className="py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[13px] font-medium text-slate-800 truncate">{b.name}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[12px] text-slate-500 tabular-nums">{dollars(b.spent)} of {dollars(b.pool)}</span>
                      <StatePill cls={st.pill}>{st.word}</StatePill>
                    </span>
                  </div>
                  <div className="relative mt-1.5 h-2 rounded-full bg-slate-100">
                    <div className="absolute inset-0 overflow-hidden rounded-full">
                      <div className={clsx('h-full rounded-full', st.bar, GROW)} style={{ transform: `scaleX(${grown ? fill : 0})` }} />
                    </div>
                    <span className="absolute -top-0.5 -bottom-0.5 w-0.5 rounded bg-slate-700/70" style={{ left: `calc(${pace * 100}% - 1px)` }} aria-hidden="true" />
                  </div>
                  {b.state === 'slow' && b.forfeit > 0 && (
                    <p className="mt-1 text-[12px] text-amber-800">
                      {quarter && quarter.daysLeft > 0
                        ? `On course to finish ${dollars(b.forfeit)} above what can be carried into next quarter.`
                        : `${dollars(b.forfeit)} more than can be carried into next quarter.`}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-500">
            <span className="inline-block w-0.5 h-3 rounded bg-slate-700/70" aria-hidden="true" />
            Where spending should be today
            {budgets.participants > budgets.list.length && <span className="ml-auto">{budgets.list.length} of {budgets.participants} shown</span>}
          </p>
          {budgets.withoutBudget > 0 && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              {plural(budgets.withoutBudget, 'participant has', 'participants have')} no budget entered for this quarter, so their spending is not tracked.
            </p>
          )}
        </>
      )}
    </Card>
  );
}
function StatePill({ cls, children }) {
  return <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset whitespace-nowrap', cls)}>{children}</span>;
}

// ── Claims and statements ────────────────────────────────────────────────────
const STAGES = [
  { key: 'draft', label: 'To lodge', dot: 'bg-slate-400' },
  { key: 'submitted', label: 'Awaiting payment', dot: 'bg-blue-500' },
  { key: 'paid', label: 'Paid', dot: 'bg-emerald-500' },
];
const STMT = {
  done: { Icon: CheckCircle2, cls: 'text-emerald-600', box: 'bg-emerald-50/60 ring-emerald-200/70' },
  overdue: { Icon: AlertCircle, cls: 'text-red-600', box: 'bg-red-50/70 ring-red-200/70' },
  soon: { Icon: Clock, cls: 'text-amber-600', box: 'bg-amber-50/70 ring-amber-200/70' },
  open: { Icon: Clock, cls: 'text-slate-500', box: 'bg-slate-50 ring-slate-200' },
  current: { Icon: Clock, cls: 'text-blue-600', box: 'bg-blue-50/60 ring-blue-200/70' },
};
function ClaimsCard({ claims, statements, today, onGoTo }) {
  const grown = useGrown();
  const months = (claims && claims.months) || [];
  const max = Math.max(1, ...months.map((m) => Math.max(m.claimed, m.paid)));
  const rejected = claims && claims.stage.rejected.n;
  const shownStatements = (statements || []).filter((s) => s.state !== 'none');
  return (
    <Card id="ac-claims-title" title="Claims and statements" icon={Landmark} onGoTo={onGoTo}
      link={{ tab: 'claims', label: 'Open claims' }}
      meta={claims && claims.paidThisQuarter > 0 ? <span className="text-[12px] text-slate-500">{dollars(claims.paidThisQuarter)} paid this quarter</span> : null}
    >
      {claims == null ? <PanelError what="the claims" /> : (
        <>
          <ol className="grid grid-cols-3 gap-2" aria-label="Claims by stage">
            {STAGES.map((s) => (
              <li key={s.key} className="rounded-lg bg-slate-50 px-3 py-2.5">
                <span className="flex items-center gap-1.5 text-[12px] text-slate-600">
                  <span className={clsx('w-2 h-2 rounded-full', s.dot)} aria-hidden="true" />{s.label}
                </span>
                <span className="block mt-1 font-display text-[18px] font-semibold tabular-nums text-slate-900">{dollars(claims.stage[s.key].amount)}</span>
                <span className="block text-[11px] text-slate-500">{plural(claims.stage[s.key].n, 'claim')}</span>
              </li>
            ))}
          </ol>
          {rejected > 0 && (
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-800">
              {plural(rejected, 'claim was', 'claims were')} rejected. Correct and lodge again from Claims.
            </p>
          )}
          {claims.unclaimed > 0 && (
            <button type="button" onClick={() => onGoTo('claims')} className="mt-2 w-full text-left rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-900 hover:bg-amber-100/70">
              {dollars(claims.unclaimed)} of care delivered in {plural(claims.unclaimedMonths, 'finished month')} has no claim yet.
            </button>
          )}

          {months.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between gap-2 text-[12px] text-slate-500">
                <span>Claimed and paid, by month</span>
                <span className="flex items-center gap-3">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm hue-bar opacity-30" aria-hidden="true" />Claimed</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm hue-bar" aria-hidden="true" />Paid</span>
                </span>
              </div>
              <div className="mt-2 flex items-end gap-3 h-24" role="img" aria-label={months.map((m) => `${monthLabel(m.period)}: claimed ${dollars(m.claimed)}, paid ${dollars(m.paid)}`).join('; ')}>
                {months.map((m) => (
                  <div key={m.period} className="flex-1 min-w-0 h-full flex flex-col justify-end" title={`${monthLabel(m.period)}: claimed ${dollars(m.claimed)}, paid ${dollars(m.paid)}`}>
                    <div className="flex items-end justify-center gap-1 h-full">
                      <div className={clsx('w-1/2 max-w-[18px] rounded-t hue-bar opacity-30 origin-bottom transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none')} style={{ height: `${(m.claimed / max) * 100}%`, transform: `scaleY(${grown ? 1 : 0})` }} />
                      <div className={clsx('w-1/2 max-w-[18px] rounded-t hue-bar origin-bottom transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none')} style={{ height: `${(m.paid / max) * 100}%`, transform: `scaleY(${grown ? 1 : 0})` }} />
                    </div>
                    <span className="mt-1 text-center text-[11px] text-slate-500">{monthShort(`${m.period}-01`)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {shownStatements.length > 0 && (
        <div className="mt-4">
          <div className="text-[12px] text-slate-500">Monthly statements</div>
          <ul className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-2">
            {shownStatements.map((s) => {
              const k = STMT[s.state] || STMT.open;
              const note = s.state === 'done' ? `All ${s.participants} issued`
                : s.state === 'current' ? `Due ${longDate(s.due)}`
                  : s.state === 'overdue' ? `${s.issued} of ${s.participants} issued, overdue`
                    : `${s.issued} of ${s.participants} issued, due ${inDays(today, s.due)}`;
              return (
                <li key={s.period}>
                  <button type="button" onClick={() => onGoTo('statements')}
                    className={clsx('w-full rounded-lg px-3 py-2 text-left ring-1 ring-inset hover:brightness-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500', k.box)}
                  >
                    <span className="flex items-center gap-1.5 text-[13px] font-medium text-slate-800">
                      <k.Icon size={14} className={k.cls} aria-hidden="true" />{monthName(s.period)}
                    </span>
                    <span className="block text-[12px] text-slate-600 mt-0.5">{note}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ── Visits this week ─────────────────────────────────────────────────────────
function VisitsCard({ visits, today, onGoTo }) {
  const grown = useGrown();
  if (visits == null) return <Card id="ac-visits-title" title="Visits this week" icon={CalendarCheck} onGoTo={onGoTo}><PanelError what="this week's visits" /></Card>;
  const max = Math.max(1, ...visits.days.map((d) => d.delivered + d.scheduled + d.cancelled));
  const total = visits.delivered + visits.scheduled;
  return (
    <Card id="ac-visits-title" title="Visits this week" icon={CalendarCheck} onGoTo={onGoTo}
      meta={<span className="text-[12px] text-slate-500">{parseDay(visits.from).getDate()} to {longDate(visits.to)}</span>}
    >
      {total + visits.cancelled === 0 ? (
        <p className="text-[13px] text-slate-500 py-6 text-center">No aged care visits are booked this week.</p>
      ) : (
        <>
          <div className="flex items-end gap-2 h-28" role="img"
            aria-label={visits.days.map((d) => `${parseDay(d.day).toLocaleDateString('en-AU', { weekday: 'long' })}: ${d.delivered} delivered, ${d.scheduled} booked, ${d.cancelled} cancelled`).join('; ')}
          >
            {visits.days.map((d) => {
              const isToday = d.day === today;
              const n = d.delivered + d.scheduled + d.cancelled;
              return (
                <div key={d.day} className="flex-1 min-w-0 h-full flex flex-col justify-end">
                  <span className="text-center text-[11px] tabular-nums text-slate-500 mb-0.5">{n || ''}</span>
                  <div className="mx-auto w-full max-w-[28px] flex flex-col-reverse overflow-hidden rounded-md origin-bottom transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none"
                    style={{ height: `${(n / max) * 100}%`, transform: `scaleY(${grown ? 1 : 0})` }}
                  >
                    {d.delivered > 0 && <div className="hue-bar" style={{ flex: d.delivered }} />}
                    {d.scheduled > 0 && <div className="hue-bar opacity-30" style={{ flex: d.scheduled }} />}
                    {d.cancelled > 0 && <div className="bg-slate-300" style={{ flex: d.cancelled }} />}
                  </div>
                  <span className={clsx('mt-1.5 text-center text-[11px]', isToday ? 'font-semibold hue-text' : 'text-slate-500')}>
                    {parseDay(d.day).toLocaleDateString('en-AU', { weekday: 'short' })}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-slate-600">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm hue-bar" aria-hidden="true" />{visits.delivered} delivered</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm hue-bar opacity-30" aria-hidden="true" />{visits.scheduled} booked</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-slate-300" aria-hidden="true" />{visits.cancelled} cancelled</span>
            <span className="ml-auto text-slate-500 tabular-nums">{visits.hours} hours</span>
          </div>
          {visits.unfilled > 0 && (
            <Link to="/scheduler" className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-900 hover:bg-amber-100/70">
              <span>{plural(visits.unfilled, 'visit')} from today on {visits.unfilled === 1 ? 'has' : 'have'} no worker yet.</span>
              <span className="inline-flex items-center gap-1 font-medium text-primary-700 whitespace-nowrap">Open the scheduler<ArrowRight size={13} aria-hidden="true" /></span>
            </Link>
          )}
        </>
      )}
    </Card>
  );
}

// ── Participants ─────────────────────────────────────────────────────────────
function ParticipantsCard({ mix, onGoTo }) {
  const grown = useGrown();
  if (mix == null) return <Card id="ac-mix-title" title="Participants" icon={Users} onGoTo={onGoTo}><PanelError what="the participants" /></Card>;
  const levels = Object.entries(mix.levels || {}).map(([k, n]) => [Number(k), n]).sort((a, b) => a[0] - b[0]);
  const max = Math.max(1, ...levels.map(([, n]) => n));
  const extras = [
    ['Restorative care', mix.restorative],
    ['End-of-life', mix.endOfLife],
    ['Assistive technology and home modifications', mix.athm],
    ['Home Care Packages', mix.hcp],
  ].filter(([, n]) => n > 0);
  return (
    <Card id="ac-mix-title" title="Participants" icon={Users} onGoTo={onGoTo} link={{ tab: 'participants', label: 'Open participants' }}
      meta={mix.newThisQuarter > 0 ? <StatePill cls="hue-chip ring-transparent">{mix.newThisQuarter} new this quarter</StatePill> : null}
    >
      {mix.total === 0 ? (
        <p className="text-[13px] text-slate-500 py-6 text-center">No participant has a funding plan yet.</p>
      ) : (
        <>
          {levels.length > 0 && (
            <>
              <div className="text-[12px] text-slate-500">Support at Home by classification</div>
              <ul className="mt-2 space-y-1.5">
                {levels.map(([lvl, n]) => (
                  <li key={lvl} className="flex items-center gap-3 text-[12px]">
                    <span className="w-24 shrink-0 text-slate-600">Classification {lvl}</span>
                    <span className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                      <span className={clsx('block h-full rounded-full hue-bar', GROW)} style={{ transform: `scaleX(${grown ? n / max : 0})`, opacity: 0.45 + (lvl / 8) * 0.55 }} />
                    </span>
                    <span className="w-6 text-right tabular-nums font-medium text-slate-800">{n}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {extras.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-1.5">
              {extras.map(([label, n]) => (
                <li key={label} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-50 ring-1 ring-inset ring-slate-200 px-2.5 py-1 text-[12px] text-slate-700">
                  <span className="font-semibold tabular-nums text-slate-900">{n}</span>{label}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}

// ── AI risk review ───────────────────────────────────────────────────────────
// Pick a participant; Claude reviews their recent incidents, complaints, leave and care management
// cadence and flags deterioration risk. Advisory only.
function RiskReviewCard() {
  const [clients, setClients] = useState([]);
  const [clientsFailed, setClientsFailed] = useState(false);
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const loadClients = useCallback(() => {
    setClientsFailed(false);
    api.get('/aged-care/clients').then((r) => setClients(r.data || [])).catch(() => setClientsFailed(true));
  }, []);
  useEffect(() => { loadClients(); }, [loadClients]);
  const run = async () => {
    if (!clientId) return toast.error('Pick a participant');
    setBusy(true); setResult(null);
    try { const r = await api.post('/aged-care/risk-review', { client_id: clientId }); setResult(r.data); }
    catch (e) { toast.error(e.response?.data?.message || 'Risk review failed'); } finally { setBusy(false); }
  };
  const tone = { low: 'bg-emerald-50 text-emerald-800 border-emerald-200', moderate: 'bg-amber-50 text-amber-800 border-amber-200', high: 'bg-red-50 text-red-800 border-red-200' };
  return (
    <Card id="ac-risk-title" title="AI risk review" icon={Sparkles}>
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <p className="text-[13px] text-slate-600 md:flex-1 max-w-[60ch]">
          Reviews a participant's SIRS incidents, complaints, hospital leave, care management, care plan and budget burn from the last six months, and flags signs of decline. Advisory only: nothing is written to the record.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 md:w-[26rem] shrink-0">
          <select
            value={clientId} onChange={(e) => setClientId(e.target.value)} aria-label="Participant to review"
            className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a participant…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button type="button" onClick={run} disabled={busy} className="btn-primary shrink-0">
            <Sparkles size={15} aria-hidden="true" /> {busy ? 'Reviewing…' : 'Run review'}
          </button>
        </div>
      </div>
      {/* Without this the participant list was just empty, as if there was nobody to review. */}
      {clientsFailed && (
        <p className="mt-2 text-[12.5px] text-red-800" role="alert">
          The participant list did not load. <button type="button" className="underline underline-offset-2" onClick={loadClients}>Try again</button>
        </p>
      )}
      {result && (
        <div className={clsx('mt-3 rounded-xl border p-3.5 text-sm whitespace-pre-wrap', tone[result.risk_level] || 'bg-slate-50 text-slate-700 border-slate-200')}>{result.review}</div>
      )}
    </Card>
  );
}

// ── Loading ──────────────────────────────────────────────────────────────────
// Shaped like what arrives, so nothing jumps when the data lands.
function Skeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      <div className="rounded-xl border border-slate-200 bg-white p-6 flex flex-col lg:flex-row gap-6">
        <div className="lg:w-[20rem] space-y-2"><div className="skeleton h-7 w-56" /><div className="skeleton h-4 w-40" /></div>
        <div className="flex-1 space-y-4 pt-1"><div className="skeleton h-3 w-full" /><div className="skeleton h-3 w-full" /><div className="skeleton h-4 w-2/3" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => <div key={i} className="rounded-xl border border-slate-200 bg-white p-4"><div className="skeleton h-4 w-28" /><div className="skeleton h-7 w-24 mt-3" /><div className="skeleton h-3 w-32 mt-2" /></div>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-3 px-4 py-3">
              <div className="skeleton w-5 h-5 rounded-full shrink-0" />
              <div className="flex-1 space-y-2"><div className="skeleton h-4 w-3/5" /><div className="skeleton h-3.5 w-4/5" /></div>
            </div>
          ))}
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4"><div className="skeleton h-4 w-40" /><div className="skeleton h-24 mt-4" /></div>
      </div>
    </div>
  );
}

export default function AgedCareDashboard({ onGoTo }) {
  // Four independent loads. Each one that fails says so, with a retry, instead of falling back to
  // an empty list: a failed load shown as "nothing is overdue" is the one thing this screen must
  // never do.
  const [state, setState] = useState({ loading: true });
  const [refreshing, setRefreshing] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  const load = useCallback(async (quiet = false) => {
    if (quiet) setRefreshing(true); else setState((s) => ({ ...s, loading: true }));
    const [a, d, o, cm] = await Promise.allSettled([
      api.get('/aged-care/attention'), api.get('/aged-care/dashboard'),
      api.get('/aged-care/overview'), api.get('/aged-care/care-management/account'),
    ]);
    if (!alive.current) return;
    const dash = d.status === 'fulfilled' ? (d.value.data || {}) : null;
    setState({
      loading: false,
      updatedAt: new Date(),
      attention: a.status === 'fulfilled' ? (a.value.data || { items: [] }) : null,
      attentionError: a.status === 'rejected'
        ? (a.reason?.response?.data?.message || 'Could not work out what needs attention.')
        : null,
      dashboard: dash && !dash.failed ? dash : null,
      overview: o.status === 'fulfilled' ? o.value.data : null,
      overviewError: o.status === 'rejected' ? (o.reason?.response?.data?.message || 'Could not load the quarter.') : null,
      cmAccount: cm.status === 'fulfilled' ? cm.value.data : null,
    });
    setRefreshing(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const go = useCallback((key) => { if (typeof onGoTo === 'function') onGoTo(key); }, [onGoTo]);

  const { loading, attention, attentionError, dashboard: dash, overview: ov, overviewError, cmAccount, updatedAt } = state;

  if (loading) {
    return (
      <div>
        <h1 className="sr-only">Aged care overview</h1>
        <p role="status" className="sr-only">Loading the overview…</p>
        <Skeleton />
      </div>
    );
  }

  const today = ov?.quarter?.today || new Date().toISOString().slice(0, 10);
  const mix = ov?.mix;
  const visits = ov?.visits;
  const delivered = ov?.delivered;
  const nothingYet = mix && mix.total === 0 && dash && !dash.activeClients;

  return (
    <div className="space-y-5">
      {/* The app header names the module; this names the screen for anyone using a screen reader. */}
      <h1 className="sr-only">Aged care overview</h1>

      {ov ? (
        <QuarterBand quarter={ov.quarter} budgets={ov.budgets} updatedAt={updatedAt} refreshing={refreshing} onRefresh={() => load(true)} onGoTo={go} />
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white">
          <ErrorState message={overviewError || 'Could not load the quarter.'} onRetry={() => load()} />
        </div>
      )}

      {nothingYet ? (
        <div className="rounded-xl border border-slate-200 bg-white">
          <EmptyState
            icon={ClipboardList}
            title="No aged care participants yet"
            hint="Add participants and give each one a Support at Home or Home Care Package funding plan. Their budgets, visits, claims and statements then show here."
            action={<button type="button" onClick={() => go('participants')} className="btn-primary">Open participants</button>}
          />
        </div>
      ) : (
        <>
          {ov && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <KpiTile
                icon={Users} accent="teal" label="Participants" value={count(mix?.total)}
                sub={mix ? (mix.hcp > 0 ? `${mix.sah} Support at Home, ${mix.hcp} Home Care Package` : mix.newThisQuarter > 0 ? `${mix.newThisQuarter} started this quarter` : 'On Support at Home') : 'Not available'}
              />
              <KpiTile
                icon={CalendarCheck} accent="teal" label="Visits this week"
                value={visits ? count(visits.delivered + visits.scheduled) : '—'}
                sub={!visits ? 'Not available' : visits.unfilled > 0 ? `${visits.unfilled} still need a worker` : `${visits.delivered} delivered, ${visits.scheduled} to come`}
                alert={!!visits && visits.unfilled > 0}
              />
              <KpiTile
                icon={Receipt} accent="teal" label="Billed this quarter" value={delivered ? dollars(delivered.price) : '—'}
                sub={delivered ? `${dollars(delivered.government)} government, ${dollars(delivered.contributions)} participants` : 'Not available'}
              />
              <KpiTile
                icon={PiggyBank} accent="teal" label="Care management account"
                value={cmAccount ? dollars(cmAccount.balance) : '—'}
                sub={cmAccount ? '10% pooled from every budget' : 'Not available'}
                alert={!!cmAccount && Number(cmAccount.balance) < 0}
              />
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            <div className="lg:col-span-2 min-w-0">
              {attentionError ? (
                <section aria-labelledby="ac-needs-title">
                  <h2 id="ac-needs-title" className="text-[17px] font-semibold text-slate-900 mb-3">What needs you</h2>
                  <div className="rounded-xl border border-slate-200 bg-white">
                    <ErrorState message={attentionError} onRetry={() => load()} />
                  </div>
                </section>
              ) : (
                <NeedsYou list={buildList(attention, dash)} onGoTo={go} onChanged={() => load(true)} />
              )}
            </div>
            <div className="space-y-5 min-w-0 lg:pt-[2.35rem]">
              {dash ? <QualityCard q={dash.quality} onGoTo={go} /> : (
                <Card id="ac-quality-title" title="Quality and compliance" icon={Activity}><PanelError what="the quality score" /></Card>
              )}
              {ov && <ComingUp items={ov.coming} today={today} onGoTo={go} />}
            </div>
          </div>

          {ov && (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
                <BudgetsCard budgets={ov.budgets} quarter={ov.quarter} onGoTo={go} />
                <ClaimsCard claims={ov.claims} statements={ov.statements} today={today} onGoTo={go} />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
                <VisitsCard visits={visits} today={today} onGoTo={go} />
                <ParticipantsCard mix={mix} onGoTo={go} />
              </div>
            </>
          )}
        </>
      )}

      <RiskReviewCard />
      <Connections />
    </div>
  );
}
